import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Stack, FormControlLabel, Switch, Checkbox, CircularProgress, Box, Typography, IconButton, Alert } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { createNews, createNewsWithGallery } from '../../controllers/newsController';
import EntityGalleryFormSection from '../../components/EntityGalleryFormSection';
import { buildCreateEntityGalleryDto, initialEntityGalleryForm } from '../../constants/galleryEntityTypes';

const CreateNewsDialog = ({ open, onClose, onCreated }) => {
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [content, setContent] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [sendPushNotification, setSendPushNotification] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [galleryForm, setGalleryForm] = useState(initialEntityGalleryForm);

  const reset = () => {
    setTitle('');
    setSummary('');
    setContent('');
    setIsActive(true);
    setSendPushNotification(false);
    setError('');
    setGalleryForm(initialEntityGalleryForm);
  };

  const handleClose = () => {
    reset();
    onClose?.();
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setError('');
    setSubmitting(true);
    try {
      const payload = {
        title,
        summary,
        content,
        isActive,
        sendPushNotification: Boolean(sendPushNotification),
      };
      const galleryDto = buildCreateEntityGalleryDto(galleryForm);
      if (galleryDto) payload.gallery = galleryDto;

      const hasGalleryImages = galleryForm.enabled && (galleryForm.files?.length || 0) > 0;
      if (hasGalleryImages) {
        await createNewsWithGallery({ dto: payload, galleryImages: galleryForm.files });
      } else {
        await createNews(payload);
      }
      onCreated?.({ sendPushNotification: Boolean(sendPushNotification) });
      handleClose();
    } catch (err) {
      setError(err?.message || 'Haber oluşturulamadı');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          fontSize: '18px',
          fontWeight: 500,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pr: 3,
        }}
      >
        Yeni Haber
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ py: 2, px: 3 }}>
          <Stack spacing={2}>
            {error && <Alert severity="error">{error}</Alert>}
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                Başlık
              </Typography>
              <TextField
                fullWidth
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                placeholder=""
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '5px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0E4D94',
                    },
                  },
                  '& .MuiInputBase-input': {
                    py: 1.2,
                    px: 1.5,
                    fontSize: '14px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e6e6e6',
                  },
                }}
              />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                Özet
              </Typography>
              <TextField
                fullWidth
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                required
                placeholder=""
                multiline
                minRows={2}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '5px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0E4D94',
                    },
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '14px',
                    px: 0,
                    py: 0,
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e6e6e6',
                  },
                }}
              />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                İçerik
              </Typography>
              <TextField
                fullWidth
                value={content}
                onChange={(e) => setContent(e.target.value)}
                required
                placeholder=""
                multiline
                minRows={6}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '5px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0E4D94',
                    },
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '14px',
                    px: 0,
                    py: 0,
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e6e6e6',
                  },
                }}
              />
            </Box>
            <EntityGalleryFormSection
              value={galleryForm}
              onChange={setGalleryForm}
              disabled={submitting}
            />
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                Durum
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#0E4D94',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#0E4D94',
                      },
                    }}
                  />
                }
                label={isActive ? 'Aktif' : 'Pasif'}
                sx={{
                  '& .MuiFormControlLabel-label': {
                    fontSize: '13px',
                  },
                }}
              />
            </Box>
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={sendPushNotification}
                    onChange={(e) => setSendPushNotification(e.target.checked)}
                    sx={{
                      color: '#a3a3a3',
                      '&.Mui-checked': {
                        color: '#0E4D94',
                      },
                    }}
                  />
                }
                label="Uygulama bildirimi gönderilsin mi?"
                sx={{
                  ml: 0,
                  '& .MuiFormControlLabel-label': {
                    fontSize: '13px',
                  },
                }}
              />
              <Typography variant="body2" sx={{ fontSize: '12px', color: '#a3a3a3', ml: 4, mt: -0.5 }}>
                İşaretlenirse haber başlığı ve özeti tüm üyelere uygulama bildirimi olarak gönderilir.
              </Typography>
            </Box>
        </Stack>
      </DialogContent>
        <DialogActions sx={{ py: 2, px: 3 }}>
          <Button
            onClick={handleClose}
            disabled={submitting}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              color: '#666666',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            İptal
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={submitting || !title || !summary || !content}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              backgroundColor: '#0E4D94',
              borderRadius: '5px',
              px: 2,
              '&:hover': {
                backgroundColor: '#0a3d7a',
              },
              '&:disabled': {
                backgroundColor: '#e6e6e6',
                color: '#a3a3a3',
              },
            }}
          >
          {submitting ? <CircularProgress size={18} color="inherit" /> : 'Oluştur'}
        </Button>
      </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateNewsDialog;
